import { createContext, use } from 'react'

import { cn, cva } from '~/utils/classnames'

import { Slot } from './slot'

type InputSize = 'sm' | 'md' | 'lg'

interface InputGroupContextType {
  inGroup: true
  size: InputSize
}

const InputGroupContext = createContext<InputGroupContextType | null>(null)

const useInputGroup = () => use(InputGroupContext)

const inputStyle = cva({
  base: [
    'transition',
    'w-full shadow-xs',
    'bg-under/2 border border-border/6 hover:border-border/12',
    'text-sm text-foreground/80 placeholder:text-foreground/48 font-mid-medium',
    // Flex for group layout (harmless on input since it has no children)
    'flex items-center gap-2',
    // Focus states - focus-within works for both input (self) and group (children)
    'focus-within:border-accent-element',
    'focus:outline-none focus-visible:text-foreground',
    // Focus ring - both selectors so it works on input and group
    'focus-visible:ring-4 focus-visible:ring-accent-element/50',
    'has-[:focus-visible]:ring-4 has-[:focus-visible]:ring-accent-element/50 has-[:focus-visible]:outline-hidden',
    // Disabled states - both direct and has-[] for input vs group
    'disabled:cursor-not-allowed disabled:opacity-50',
    'has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50',
    // Invalid states - both direct and has-[] for input vs group
    'data-[invalid=true]:border-destructive data-[invalid=true]:hover:border-destructive data-[invalid=true]:focus-visible:border-destructive data-[invalid=true]:focus-visible:ring-destructive/20!',
    'has-[[data-invalid=true]]:border-destructive has-[[data-invalid=true]]:hover:border-destructive',
    'has-[[data-invalid=true]]:focus-within:border-destructive has-[[data-invalid=true]]:focus-within:ring-destructive/20!',
    // In-group overrides using parent selector
    '[[data-input-group]_&]:flex-1 [[data-input-group]_&]:min-w-0 [[data-input-group]_&]:h-full',
    '[[data-input-group]_&]:bg-transparent [[data-input-group]_&]:border-0 [[data-input-group]_&]:shadow-none [[data-input-group]_&]:p-0',
    '[[data-input-group]_&]:rounded-none',
    '[[data-input-group]_&]:focus:outline-none [[data-input-group]_&]:focus:ring-0 [[data-input-group]_&]:focus-visible:ring-0!',
    '[[data-input-group]_&]:hover:border-0',
  ],
  variants: {
    size: {
      sm: 'h-8 px-2 rounded-md',
      md: 'h-9 px-3 rounded-md',
      lg: 'h-12 px-3 rounded-lg',
    },
  },
  defaultVariants: {
    size: 'md',
  },
})

interface InputProps extends Omit<
  React.ComponentPropsWithRef<'input'>,
  'size'
> {
  invalid?: boolean
  size?: InputSize
}

const InputRoot = ({ className, size, invalid, ...props }: InputProps) => {
  const groupContext = useInputGroup()
  const inGroup = groupContext?.inGroup ?? false

  if (process.env.NODE_ENV !== 'production' && inGroup && size !== undefined) {
    throw new Error(
      'Input: The `size` prop should be passed to `Input.Group` instead of `Input` when inside a group.'
    )
  }

  const effectiveSize = inGroup ? groupContext!.size : (size ?? 'md')

  return (
    <input
      className={cn(inputStyle({ size: effectiveSize }), className)}
      data-invalid={invalid}
      aria-invalid={invalid}
      {...props}
    />
  )
}

interface InputGroupProps extends Omit<
  React.ComponentPropsWithRef<'div'>,
  'size'
> {
  size?: InputSize
}

const InputGroup = ({
  className,
  size = 'md',
  children,
  ...props
}: InputGroupProps) => {
  return (
    <InputGroupContext value={{ inGroup: true, size }}>
      <div
        data-input-group
        className={cn(inputStyle({ size }), className)}
        {...props}
      >
        {children}
      </div>
    </InputGroupContext>
  )
}

interface InputAddonProps extends React.ComponentPropsWithRef<'div'> {
  asChild?: boolean
}

const InputPrefix = ({ className, asChild, ...props }: InputAddonProps) => {
  const Comp = asChild ? Slot : 'div'

  return (
    <Comp
      data-input-addon
      className={cn(
        'flex shrink-0 items-center justify-center',
        'text-foreground/64',
        className
      )}
      {...props}
    />
  )
}

const InputSuffix = ({ className, asChild, ...props }: InputAddonProps) => {
  const Comp = asChild ? Slot : 'div'

  return (
    <Comp
      data-input-addon
      className={cn(
        'flex shrink-0 items-center justify-center',
        'text-foreground/64',
        className
      )}
      {...props}
    />
  )
}

const Input = Object.assign(InputRoot, {
  Group: InputGroup,
  Prefix: InputPrefix,
  Suffix: InputSuffix,
})

export { Input, inputStyle }
